import React from 'react';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { Plus, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

// Map model IDs to short display names
const MODEL_SHORT_NAMES: Record<string, string> = {
  'claude-sonnet-4-5': 'Sonnet',
  'claude-opus-4-1': 'Opus',
  'gpt-5': 'GPT5',
  'gpt-5-mini': 'GPT5m',
  'gpt-5-nano': 'GPT5n',
  'claude-3-5-haiku-latest': 'Haiku',
  'grok-code-fast-1': 'Grok',
};

const getModelShortName = (modelId: string): string => {
  return MODEL_SHORT_NAMES[modelId] || modelId.split('-')[0];
};

interface Conversation {
  id: string;
  title: string;
  created: string;
  updated: string;
  last_message_at?: string;
  active_model?: string;
}

interface ConversationSidebarProps {
  conversations: Conversation[];
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  loading?: boolean;
  className?: string;
}

const formatMessageTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  
  if (diffInHours < 1) {
    return formatDistanceToNow(date, { addSuffix: true });
  } else if (isToday(date)) {
    return format(date, 'h:mm a');
  } else if (isYesterday(date)) {
    return 'Yesterday';
  } else if (diffInHours < 24 * 7) {
    return format(date, 'EEEE');
  } else {
    return format(date, 'MMM d');
  }
};

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  selectedConversationId,
  onSelectConversation,
  onNewConversation,
  loading = false,
  className = '',
}) => {
  const containerClassName = `w-full h-full bg-gray-50 border-r border-gray-200 flex flex-col ${className}`.trim();

  return (
    <div className={containerClassName}>
      <div className="p-4 border-b border-gray-200">
        <Button
          onClick={onNewConversation}
          className="w-full flex items-center justify-center gap-2"
          variant="default"
        >
          <Plus className="h-4 w-4" />
          New Conversation
        </Button>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-2">
            {/* Skeleton loaders for conversations */}
            {[...Array(3)].map((_, i) => (
              <div key={i} className="p-3 mb-1">
                <div className="flex items-start gap-2">
                  <Skeleton className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No conversations yet
          </div>
        ) : (
          <div className="p-2">
            {conversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => onSelectConversation(conversation.id)}
                className={`w-full text-left p-3 mb-1 rounded-lg transition-colors ${
                  selectedConversationId === conversation.id
                    ? 'bg-blue-100 text-blue-900'
                    : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-start gap-2">
                  <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {conversation.title}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="text-xs text-gray-500">
                        {formatMessageTime(conversation.last_message_at || conversation.updated)}
                      </div>
                      {conversation.active_model && (
                        <div className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          {getModelShortName(conversation.active_model)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
